/**
 * Export utilities for various geospatial formats
 */

import { exportAsShapefile } from './shapefileExport.js';
import tokml from 'tokml';
import proj4 from 'proj4';
import * as XLSX from 'xlsx';

// Define common projections
proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs');

/**
 * Reproject GeoJSON to target projection
 * @param {Object} geojson - Source GeoJSON (assumed to be in WGS84)
 * @param {string} targetProjection - Target projection (EPSG code or WKT)
 * @param {string} targetName - Friendly name for target projection
 * @returns {Object} Reprojected GeoJSON
 */
function reprojectGeoJSON(geojson, targetProjection, targetName) {
    if (!targetProjection || targetProjection === 'EPSG:4326' || targetProjection === 'WGS84') {
        // No reprojection needed
        return geojson;
    }

    try {
        // Define target projection
        if (!targetProjection.startsWith('EPSG:')) {
            // Assume it's WKT
            proj4.defs('EXPORT_TARGET', targetProjection);
            targetProjection = 'EXPORT_TARGET';
        }

        // Create transformation (from WGS84 to target)
        const transform = proj4('EPSG:4326', targetProjection);

        // Clone GeoJSON
        const reprojected = JSON.parse(JSON.stringify(geojson));

        // Transform all coordinates
        reprojected.features.forEach(feature => {
            if (feature.geometry) {
                transformGeometryForExport(feature.geometry, transform);
            }
        });

        console.log(`Reprojected to ${targetName} for export`);
        return reprojected;
    } catch (error) {
        console.error('Failed to reproject for export:', error);
        throw new Error(`Failed to reproject to ${targetName}: ${error.message}`);
    }
}

/**
 * Transform geometry coordinates for export
 * @param {Object} geometry - GeoJSON geometry
 * @param {Function} transform - proj4 transform function
 */
function transformGeometryForExport(geometry, transform) {
    if (!geometry || !geometry.type) return;

    switch (geometry.type) {
        case 'Point':
            geometry.coordinates = transform.forward(geometry.coordinates);
            break;
        case 'MultiPoint':
        case 'LineString':
            geometry.coordinates = geometry.coordinates.map(coord => transform.forward(coord));
            break;
        case 'MultiLineString':
        case 'Polygon':
            geometry.coordinates = geometry.coordinates.map(ring =>
                ring.map(coord => transform.forward(coord))
            );
            break;
        case 'MultiPolygon':
            geometry.coordinates = geometry.coordinates.map(polygon =>
                polygon.map(ring =>
                    ring.map(coord => transform.forward(coord))
                )
            );
            break;
        case 'GeometryCollection':
            geometry.geometries.forEach(geom => transformGeometryForExport(geom, transform));
            break;
    }
}

/**
 * Export layer as GeoJSON
 */
export function exportAsGeoJSON(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.geojson$/i, '').replace(/\.json$/i, '');

    // Reproject if needed
    const exportData = projection ? reprojectGeoJSON(geojson, projection, projectionName) : geojson;

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });

    downloadBlob(blob, `${cleanFileName}.geojson`);
}

/**
 * Export layer as KML
 */
export function exportAsKML(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.kml$/i, '');

    try {
        // KML is always in WGS84, so reproject to WGS84 if needed
        const exportData = projection && projection !== 'EPSG:4326'
            ? reprojectGeoJSON(geojson, 'EPSG:4326', 'WGS84')
            : geojson;

        const kmlString = tokml(exportData);
        const blob = new Blob([kmlString], { type: 'application/vnd.google-earth.kml+xml' });

        downloadBlob(blob, `${cleanFileName}.kml`);
    } catch (error) {
        console.error('Error converting to KML:', error);
        throw new Error('Failed to export as KML');
    }
}

/**
 * Export layer as GPX
 */
export function exportAsGPX(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.gpx$/i, '');

    try {
        // GPX is always in WGS84, so reproject to WGS84 if needed
        const exportData = projection && projection !== 'EPSG:4326'
            ? reprojectGeoJSON(geojson, 'EPSG:4326', 'WGS84')
            : geojson;

        const gpxString = geojsonToGPX(exportData);
        const blob = new Blob([gpxString], { type: 'application/gpx+xml' });

        downloadBlob(blob, `${cleanFileName}.gpx`);
    } catch (error) {
        console.error('Error converting to GPX:', error);
        throw new Error('Failed to export as GPX');
    }
}

/**
 * Convert GeoJSON to GPX format
 */
function geojsonToGPX(geojson) {
    let gpx = '<?xml version="1.0" encoding="UTF-8"?>\n';
    gpx += '<gpx version="1.1" creator="MapDataX" xmlns="http://www.topografix.com/GPX/1/1">\n';

    if (!geojson.features || geojson.features.length === 0) {
        gpx += '</gpx>';
        return gpx;
    }

    geojson.features.forEach(feature => {
        const geom = feature.geometry;
        const props = feature.properties || {};

        if (geom.type === 'Point') {
            // Export as waypoint
            const [lon, lat, ele] = geom.coordinates;
            gpx += '  <wpt lat="' + lat + '" lon="' + lon + '">\n';
            if (ele !== undefined) gpx += '    <ele>' + ele + '</ele>\n';
            if (props.name) gpx += '    <name>' + escapeXML(props.name) + '</name>\n';
            if (props.description) gpx += '    <desc>' + escapeXML(props.description) + '</desc>\n';
            if (props.comment) gpx += '    <cmt>' + escapeXML(props.comment) + '</cmt>\n';
            if (props.symbol) gpx += '    <sym>' + escapeXML(props.symbol) + '</sym>\n';
            if (props.time) gpx += '    <time>' + props.time + '</time>\n';
            gpx += '  </wpt>\n';
        } else if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
            // Export as track
            const lines = geom.type === 'LineString' ? [geom.coordinates] : geom.coordinates;

            gpx += '  <trk>\n';
            if (props.name) gpx += '    <name>' + escapeXML(props.name) + '</name>\n';
            if (props.description) gpx += '    <desc>' + escapeXML(props.description) + '</desc>\n';
            if (props.comment) gpx += '    <cmt>' + escapeXML(props.comment) + '</cmt>\n';

            lines.forEach(line => {
                gpx += '    <trkseg>\n';
                line.forEach(coord => {
                    const [lon, lat, ele] = coord;
                    gpx += '      <trkpt lat="' + lat + '" lon="' + lon + '">\n';
                    if (ele !== undefined) gpx += '        <ele>' + ele + '</ele>\n';
                    gpx += '      </trkpt>\n';
                });
                gpx += '    </trkseg>\n';
            });
            gpx += '  </trk>\n';
        } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            // Export polygon boundary as track
            const polygons = geom.type === 'Polygon' ? [geom.coordinates] : geom.coordinates;

            polygons.forEach((polygon, pIdx) => {
                gpx += '  <trk>\n';
                const name = props.name || 'Polygon';
                gpx += '    <name>' + escapeXML(name) + (polygons.length > 1 ? ' ' + (pIdx + 1) : '') + '</name>\n';
                if (props.description) gpx += '    <desc>' + escapeXML(props.description) + '</desc>\n';

                // Only export outer ring
                const ring = polygon[0];
                gpx += '    <trkseg>\n';
                ring.forEach(coord => {
                    const [lon, lat, ele] = coord;
                    gpx += '      <trkpt lat="' + lat + '" lon="' + lon + '">\n';
                    if (ele !== undefined) gpx += '        <ele>' + ele + '</ele>\n';
                    gpx += '      </trkpt>\n';
                });
                gpx += '    </trkseg>\n';
                gpx += '  </trk>\n';
            });
        }
    });

    gpx += '</gpx>';
    return gpx;
}

/**
 * Escape XML special characters
 */
function escapeXML(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/**
 * Export layer as CSV
 */
export function exportAsCSV(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.csv$/i, '');

    // Reproject if needed
    const exportData = projection ? reprojectGeoJSON(geojson, projection, projectionName) : geojson;

    if (!exportData.features || exportData.features.length === 0) {
        throw new Error('No features to export');
    }

    // Get all unique property keys
    const propertyKeys = new Set();
    exportData.features.forEach(feature => {
        if (feature.properties) {
            Object.keys(feature.properties).forEach(key => {
                if (!key.startsWith('__')) { // Skip internal properties
                    propertyKeys.add(key);
                }
            });
        }
    });

    // Determine if we should add lat/lon columns (for point features)
    const hasPoints = exportData.features.some(f =>
        f.geometry?.type === 'Point' || f.geometry?.type === 'MultiPoint'
    );

    // Build headers
    const geometryHeaders = ['geometry_type'];
    if (hasPoints) {
        geometryHeaders.push('longitude', 'latitude');
    }
    geometryHeaders.push('wkt'); // Well-Known Text representation
    const headers = [...geometryHeaders, ...Array.from(propertyKeys)];

    // Build CSV rows
    const rows = [headers.join(',')];

    exportData.features.forEach(feature => {
        const row = [];

        // Add geometry type
        row.push(feature.geometry?.type || '');

        // Add coordinates for points
        if (hasPoints) {
            if (feature.geometry?.type === 'Point') {
                const [lon, lat] = feature.geometry.coordinates;
                row.push(lon);
                row.push(lat);
            } else if (feature.geometry?.type === 'MultiPoint') {
                const [lon, lat] = feature.geometry.coordinates[0] || [null, null];
                row.push(lon);
                row.push(lat);
            } else {
                row.push('');
                row.push('');
            }
        }

        // Add WKT (simplified version)
        const wkt = geometryToWKT(feature.geometry);
        const escapedWkt = wkt.replace(/"/g, '""');
        row.push(`"${escapedWkt}"`);

        // Add properties
        propertyKeys.forEach(key => {
            const value = feature.properties?.[key] ?? '';
            // Escape values that contain commas or quotes
            const escaped = String(value).replace(/"/g, '""');
            row.push(escaped.includes(',') || escaped.includes('"') || escaped.includes('\n') ? `"${escaped}"` : escaped);
        });

        rows.push(row.join(','));
    });

    const csvString = rows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(blob, `${cleanFileName}.csv`);
}

/**
 * Export layer as TXT (tab-delimited)
 */
export function exportAsTXT(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.txt$/i, '');

    // Reproject if needed
    const exportData = projection ? reprojectGeoJSON(geojson, projection, projectionName) : geojson;

    if (!exportData.features || exportData.features.length === 0) {
        throw new Error('No features to export');
    }

    // Get all unique property keys
    const propertyKeys = new Set();
    exportData.features.forEach(feature => {
        if (feature.properties) {
            Object.keys(feature.properties).forEach(key => {
                if (!key.startsWith('__')) { // Skip internal properties
                    propertyKeys.add(key);
                }
            });
        }
    });

    // Determine if we should add lat/lon columns (for point features)
    const hasPoints = exportData.features.some(f =>
        f.geometry?.type === 'Point' || f.geometry?.type === 'MultiPoint'
    );

    // Build headers
    const geometryHeaders = ['geometry_type'];
    if (hasPoints) {
        geometryHeaders.push('longitude', 'latitude');
    }
    geometryHeaders.push('wkt'); // Well-Known Text representation
    const headers = [...geometryHeaders, ...Array.from(propertyKeys)];

    // Build TXT rows (tab-delimited)
    const rows = [headers.join('\t')];

    exportData.features.forEach(feature => {
        const row = [];

        // Add geometry type
        row.push(feature.geometry?.type || '');

        // Add coordinates for points
        if (hasPoints) {
            if (feature.geometry?.type === 'Point') {
                const [lon, lat] = feature.geometry.coordinates;
                row.push(lon);
                row.push(lat);
            } else if (feature.geometry?.type === 'MultiPoint') {
                const [lon, lat] = feature.geometry.coordinates[0] || [null, null];
                row.push(lon);
                row.push(lat);
            } else {
                row.push('');
                row.push('');
            }
        }

        // Add WKT (simplified version)
        const wkt = geometryToWKT(feature.geometry);
        const escapedWkt = wkt.replace(/"/g, '""');
        row.push(`"${escapedWkt}"`);

        // Add properties
        propertyKeys.forEach(key => {
            const value = feature.properties?.[key] ?? '';
            // Escape values that contain tabs or quotes
            const escaped = String(value).replace(/"/g, '""');
            row.push(escaped.includes('\t') || escaped.includes('"') || escaped.includes('\n') ? `"${escaped}"` : escaped);
        });

        rows.push(row.join('\t'));
    });

    const txtString = rows.join('\n');
    const blob = new Blob([txtString], { type: 'text/plain;charset=utf-8;' });

    downloadBlob(blob, `${cleanFileName}.txt`);
}

/**
 * Export layer as Excel (.xlsx)
 */
export function exportAsExcel(geojson, fileName, projection = null, projectionName = null) {
    const cleanFileName = fileName.replace(/\.zip$/i, '').replace(/\.xlsx$/i, '').replace(/\.xls$/i, '');

    // Reproject if needed
    const exportData = projection ? reprojectGeoJSON(geojson, projection, projectionName) : geojson;

    if (!exportData.features || exportData.features.length === 0) {
        throw new Error('No features to export');
    }

    // Get all unique property keys
    const propertyKeys = new Set();
    exportData.features.forEach(feature => {
        if (feature.properties) {
            Object.keys(feature.properties).forEach(key => {
                if (!key.startsWith('__')) { // Skip internal properties
                    propertyKeys.add(key);
                }
            });
        }
    });

    // Determine if we should add lat/lon columns (for point features)
    const hasPoints = exportData.features.some(f =>
        f.geometry?.type === 'Point' || f.geometry?.type === 'MultiPoint'
    );

    // Build headers
    const geometryHeaders = ['geometry_type'];
    if (hasPoints) {
        geometryHeaders.push('longitude', 'latitude');
    }
    geometryHeaders.push('wkt'); // Well-Known Text representation
    const headers = [...geometryHeaders, ...Array.from(propertyKeys)];

    // Build data rows
    const rows = [headers];

    exportData.features.forEach(feature => {
        const row = [];

        // Add geometry type
        row.push(feature.geometry?.type || '');

        // Add coordinates for points
        if (hasPoints) {
            if (feature.geometry?.type === 'Point') {
                const [lon, lat] = feature.geometry.coordinates;
                row.push(lon);
                row.push(lat);
            } else if (feature.geometry?.type === 'MultiPoint') {
                const [lon, lat] = feature.geometry.coordinates[0] || [null, null];
                row.push(lon);
                row.push(lat);
            } else {
                row.push('');
                row.push('');
            }
        }

        // Add WKT
        const wkt = geometryToWKT(feature.geometry);
        row.push(wkt);

        // Add properties
        propertyKeys.forEach(key => {
            const value = feature.properties?.[key] ?? '';
            row.push(value);
        });

        rows.push(row);
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    downloadBlob(blob, `${cleanFileName}.xlsx`);
}

/**
 * Convert GeoJSON geometry to WKT format
 */
function geometryToWKT(geometry) {
    if (!geometry) return '';

    const coords = geometry.coordinates;
    const type = geometry.type;

    const formatCoord = (coord) => `${coord[0]} ${coord[1]}`;
    const formatCoordList = (coords) => coords.map(formatCoord).join(', ');

    switch (type) {
        case 'Point':
            return `POINT (${formatCoord(coords)})`;
        case 'MultiPoint':
            return `MULTIPOINT (${coords.map(c => `(${formatCoord(c)})`).join(', ')})`;
        case 'LineString':
            return `LINESTRING (${formatCoordList(coords)})`;
        case 'MultiLineString':
            return `MULTILINESTRING (${coords.map(line => `(${formatCoordList(line)})`).join(', ')})`;
        case 'Polygon':
            return `POLYGON (${coords.map(ring => `(${formatCoordList(ring)})`).join(', ')})`;
        case 'MultiPolygon':
            return `MULTIPOLYGON (${coords.map(poly =>
                `(${poly.map(ring => `(${formatCoordList(ring)})`).join(', ')})`
            ).join(', ')})`;
        default:
            return JSON.stringify(coords).replace(/,/g, ';');
    }
}

/**
 * Export layer in the specified format
 */
export async function exportLayer(geojson, fileName, format, metadata = null, projection = null, projectionName = null) {
    switch (format) {
        case 'shp':
            await exportAsShapefile(geojson, fileName, metadata, projection, projectionName);
            break;
        case 'geojson':
            exportAsGeoJSON(geojson, fileName, projection, projectionName);
            break;
        case 'kml':
            exportAsKML(geojson, fileName, projection, projectionName);
            break;
        case 'gpx':
            exportAsGPX(geojson, fileName, projection, projectionName);
            break;
        case 'csv':
            exportAsCSV(geojson, fileName, projection, projectionName);
            break;
        case 'txt':
            exportAsTXT(geojson, fileName, projection, projectionName);
            break;
        case 'xlsx':
            exportAsExcel(geojson, fileName, projection, projectionName);
            break;
        default:
            throw new Error(`Unsupported export format: ${format}`);
    }
}

/**
 * Helper function to download a blob
 */
function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
